import {clsx} from 'clsx'
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  isValidElement,
  Children,
  type ReactNode,
  type HTMLAttributes,
} from 'react'
import {useSlots} from '@primer/react/experimental'
import {ContextualGlobalSsoBanner} from '@github-ui/global-sso-banner-ui/contextual'
import {useMediaQuery} from '@github-ui/use-media-query'
import styles from './Layout.module.css'
import {Header, HeaderLeft, HeaderRight} from './Header'
import {HeaderToggleOnly} from './HeaderToggleOnly'
import {Sidebar, type SidebarHeaderOverrideProps} from './Sidebar'
import {LayoutContext, useLayoutContext} from './layout-context'
import type {DesktopLayoutState} from './types'

function hasHeaderColumns(headerElement: React.ReactElement | null): boolean {
  if (!headerElement || !isValidElement(headerElement)) return false

  let hasLeft = false
  let hasRight = false

  const children = (headerElement.props as {children?: React.ReactNode}).children
  for (const child of Children.toArray(children)) {
    if (isValidElement(child)) {
      if (child.type === HeaderLeft) hasLeft = true
      if (child.type === HeaderRight) hasRight = true
    }
  }

  return hasLeft && hasRight
}

interface LayoutSidebarBaseProps {
  children?: ReactNode
  className?: string
  /**
   * Additional class name to apply to the inner sidebar container div.
   */
  innerClassName?: string
  'data-testid'?: string
}

export type LayoutSidebarProps = LayoutSidebarBaseProps & SidebarHeaderOverrideProps

function LayoutSidebar({
  children,
  className,
  innerClassName,
  'data-testid': dataTestId,
  header,
  'aria-labelledby': ariaLabelledBy,
}: LayoutSidebarProps) {
  const layoutContext = useLayoutContext()
  const {sidebarExpanded, sidebarVisible, sidebarCondensable, toggleSidebar, sidebarRef} = layoutContext

  const sidebarProps = header === undefined ? {header} : {header, 'aria-labelledby': ariaLabelledBy}

  return (
    <Sidebar
      className={clsx(styles.sidebar, className)}
      innerClassName={innerClassName}
      isVisible={sidebarVisible}
      isExpanded={sidebarExpanded}
      isCondensable={sidebarCondensable}
      onToggle={toggleSidebar}
      sidebarRef={sidebarRef}
      data-testid={dataTestId}
      {...sidebarProps}
    >
      {children}
    </Sidebar>
  )
}

export interface ColumnLeftProps extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
  children: ReactNode
  className?: string
}

function ColumnLeft({children, className, ...props}: ColumnLeftProps) {
  const {mainColumn, slimLineTopTabs} = useLayoutContext()
  const isMain = mainColumn === 'left'

  return (
    <div
      className={clsx(
        styles.columnLeft,
        isMain ? styles.mainColumn : styles.secondaryColumn,
        !isMain && slimLineTopTabs && styles.slimLineTopTabs,
        className,
      )}
      data-column-position="left"
      {...props}
    >
      {children}
    </div>
  )
}

export interface ColumnRightProps extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
  children: ReactNode
  className?: string
  'data-testid'?: string
}

function ColumnRight({children, className, 'data-testid': dataTestId, ...props}: ColumnRightProps) {
  const {mainColumn, slimLineTopTabs} = useLayoutContext()
  const isMain = mainColumn === 'right'

  return (
    <div
      className={clsx(
        styles.columnRight,
        isMain ? styles.mainColumn : styles.secondaryColumn,
        !isMain && slimLineTopTabs && styles.slimLineTopTabs,
        className,
      )}
      data-testid={dataTestId}
      data-column-position="right"
      {...props}
    >
      {children}
    </div>
  )
}

function Banner() {
  return (
    <div className={styles.banner}>
      <ContextualGlobalSsoBanner />
    </div>
  )
}

/**
 * Define the breakpoint as a constant that can be shared.
 * Has to match the media query used in CSS
 */
export const LAYOUT_BREAKPOINT = '(max-width: 1024px)'

/**
 * Custom hook for tracking resize events using ResizeObserver
 * @param target - The ref to the element to observe
 * @param callback - Optional callback when resize events occur
 * @returns boolean indicating if a resize is currently happening
 */
function useResizeObserver(
  target: React.RefObject<HTMLElement | null>,
  callback?: (entry: ResizeObserverEntry) => void,
): boolean {
  const [isResizing, setIsResizing] = useState(false)
  const resizeTimeoutRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (!target.current) return

    const resizeObserver = new ResizeObserver(entries => {
      setIsResizing(true)

      // Clear any existing timeout
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current)
      }

      // Set a timeout to mark resizing as complete
      resizeTimeoutRef.current = window.setTimeout(() => {
        setIsResizing(false)
      }, 150) // 150ms debounce

      if (callback && entries[0]) {
        callback(entries[0])
      }
    })

    resizeObserver.observe(target.current)

    return () => {
      resizeObserver.disconnect()
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current)
      }
    }
  }, [target, callback])

  return isResizing
}

export interface LayoutProps {
  children: ReactNode
  className?: string
  mainColumn?: 'left' | 'right'
  'data-testid'?: string
  /**
   * Optional state to give control over the secondary column visibility to the parent.
   */
  secondaryColumnState?: [boolean, (visible: boolean) => void]
  /**
   * Optional state to give control over the resource panel visibility to the parent.
   */
  resourcePanelState?: [boolean, (visible: boolean) => void]
  faviconOverride?: string
  /**
   * Direction of the secondary column entrance animation on mobile.
   * horizontal (default) slides in from the right.
   * vertical keeps the column positioned and applies a subtle vertical translate.
   */
  mobileSecondaryColumnDirection?: 'horizontal' | 'vertical'
  /**
   * When true (default), the sidebar can be condensed on large screens.
   * When false, the sidebar toggle button is disabled and the sidebar stays expanded.
   */
  sidebarCondensable?: boolean
  /**
   * When true, applies slim line styling to top tabs.
   */
  slimLineTopTabs?: boolean
  /**
   * When true, suppresses the default floating sidebar toggle that renders when no
   * `Layout.Header` slot is provided. Use this when the consumer renders its own
   * sidebar toggle (e.g. inside a custom sticky header) to avoid a duplicate control.
   */
  hideDefaultSidebarToggle?: boolean
}

const SIDEBAR_EXPANDED_KEY = 'COPILOT_IMMERSIVE_SIDEBAR_COLLAPSED_STATE_KEY'

function getSidebarExpandedState(): boolean {
  if (typeof window === 'undefined') return true
  try {
    const value = localStorage.getItem(SIDEBAR_EXPANDED_KEY)
    return value === null ? true : JSON.parse(value)
  } catch {
    return true
  }
}

function setSidebarExpandedState(expanded: boolean): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(SIDEBAR_EXPANDED_KEY, JSON.stringify(expanded))
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Custom hook for layout state management
 * Handles sidebar visibility and expansion states, as well as secondary column visibility.
 * Responsive behavior is handled via CSS media queries.
 */
function useLayoutState(
  mainColumn?: 'left' | 'right',
  secondaryColumnState?: [boolean, (visible: boolean) => void],
  resourcePanelState?: [boolean, (visible: boolean) => void],
  sidebarCondensable = true,
  slimLineTopTabs = false,
) {
  const [sidebarExpanded, setSidebarExpanded] = useState(() => (sidebarCondensable ? getSidebarExpandedState() : true))
  const [sidebarVisible, setSidebarVisible] = useState(false)
  const internalSecondaryColumnState = useState(false)
  const internalResourcePanelState = useState(false)

  const [secondaryColumnVisible, setSecondaryColumnVisible] = secondaryColumnState ?? internalSecondaryColumnState
  const [resourcePanelOpen, setResourcePanelOpen] = resourcePanelState ?? internalResourcePanelState
  const [secondaryColumnFullWidth, setSecondaryColumnFullWidth] = useState(false)

  // Save desktop state when going mobile, restore when going back to desktop
  const desktopStateRef = useRef({visible: false, resourcePanel: false, fullWidth: false})

  const handleBreakpointChange = useCallback(
    (isMobile: boolean) => {
      if (isMobile) {
        // Save desktop state
        desktopStateRef.current = {
          visible: secondaryColumnVisible,
          resourcePanel: resourcePanelOpen,
          fullWidth: secondaryColumnFullWidth,
        }
        // Hide sidebar on mobile
        setSidebarVisible(false)
        // Only hide secondary column if not in full width mode
        if (!secondaryColumnFullWidth) {
          setSecondaryColumnVisible(false)
        }
      } else {
        // Restore desktop state
        setSecondaryColumnVisible(desktopStateRef.current.visible)
        setResourcePanelOpen(desktopStateRef.current.resourcePanel)
        setSecondaryColumnFullWidth(desktopStateRef.current.fullWidth)
      }
    },
    [
      secondaryColumnVisible,
      resourcePanelOpen,
      secondaryColumnFullWidth,
      setSecondaryColumnVisible,
      setResourcePanelOpen,
    ],
  )

  const layoutBreakpoint = useMediaQuery(LAYOUT_BREAKPOINT, handleBreakpointChange)

  // Refs for container and sidebar elements
  const containerRef = useRef<HTMLDivElement>(null)
  const sidebarRef = useRef<HTMLDivElement>(null)

  // Handle toggling sidebar state
  const toggleSidebar = useCallback(() => {
    // For floating mode (handled by CSS), toggle visibility
    // For expanded mode, toggle expansion and persist to localStorage
    if (layoutBreakpoint) {
      setSidebarVisible(!sidebarVisible)
    } else if (sidebarCondensable) {
      const newExpandedState = !sidebarExpanded
      setSidebarExpanded(newExpandedState)
      setSidebarExpandedState(newExpandedState)
    }
  }, [sidebarVisible, sidebarExpanded, layoutBreakpoint, sidebarCondensable])

  // Handle toggling secondary column state
  const toggleSecondaryColumn = useCallback(() => {
    if (secondaryColumnVisible) {
      setResourcePanelOpen(false)
    }
    setSecondaryColumnVisible(!secondaryColumnVisible)
  }, [secondaryColumnVisible, setResourcePanelOpen, setSecondaryColumnVisible])

  // Handle toggling resource panel state
  const toggleResourcePanel = useCallback(() => {
    setResourcePanelOpen(!resourcePanelOpen)
  }, [resourcePanelOpen, setResourcePanelOpen])

  // Handle toggling secondary column full width state
  const toggleSecondaryColumnFullWidth = useCallback(() => {
    setSecondaryColumnFullWidth(!secondaryColumnFullWidth)
  }, [secondaryColumnFullWidth])

  // Allow external updates to desktop layout state
  const setDesktopLayoutState = useCallback((updater: (prev: DesktopLayoutState) => DesktopLayoutState) => {
    desktopStateRef.current = updater(desktopStateRef.current)
  }, [])

  // Set up click outside behavior for floating sidebar
  useEffect(() => {
    if (sidebarVisible && sidebarRef.current) {
      const handleClickOutside = (event: MouseEvent | TouchEvent) => {
        if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
          setSidebarVisible(false)
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('touchstart', handleClickOutside, {passive: true})

      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('touchstart', handleClickOutside)
      }
    }
  }, [sidebarVisible])

  return {
    sidebarExpanded,
    sidebarVisible,
    sidebarCondensable,
    toggleSidebar,
    secondaryColumnVisible,
    toggleSecondaryColumn,
    secondaryColumnFullWidth,
    toggleSecondaryColumnFullWidth,
    resourcePanelOpen,
    toggleResourcePanel,
    setDesktopLayoutState,
    layoutBreakpoint,
    mainColumn,
    slimLineTopTabs,
    containerRef,
    sidebarRef,
  }
}

function LayoutRoot({
  children,
  className,
  mainColumn = 'left',
  'data-testid': dataTestId,
  secondaryColumnState,
  resourcePanelState,
  faviconOverride,
  mobileSecondaryColumnDirection = 'horizontal',
  sidebarCondensable = true,
  slimLineTopTabs = false,
  hideDefaultSidebarToggle = false,
  ...props
}: LayoutProps) {
  const [slots] = useSlots(children, {
    banner: Banner,
    header: Header,
    sidebar: LayoutSidebar,
    columnLeft: ColumnLeft,
    columnRight: ColumnRight,
  })

  const headerHasColumns = hasHeaderColumns(slots.header || null)
  const hasOnlyOneColumn = (!!slots.columnLeft && !slots.columnRight) || (!slots.columnLeft && !!slots.columnRight)
  const hasTwoColumns = !!slots.columnLeft && !!slots.columnRight
  const isSplitView = headerHasColumns
  const isPanelView = !headerHasColumns && !hasOnlyOneColumn

  const layoutState = useLayoutState(
    mainColumn,
    secondaryColumnState,
    resourcePanelState,
    sidebarCondensable,
    slimLineTopTabs,
  )
  const {containerRef, secondaryColumnVisible, secondaryColumnFullWidth, sidebarExpanded, resourcePanelOpen} =
    layoutState
  const isResizing = useResizeObserver(containerRef)
  return (
    <LayoutContext value={layoutState}>
      <div
        className={clsx(
          styles.container,
          slimLineTopTabs && styles.slimLineTopTabs,
          isPanelView && styles.panelView,
          isSplitView && styles.splitView,
          isSplitView && mainColumn === 'right' && styles.mainColumnRight,
          secondaryColumnVisible && styles.secondaryColumnVisible,
          !secondaryColumnVisible && hasTwoColumns && styles.secondaryColumnHidden,
          secondaryColumnFullWidth && styles.secondaryColumnFullWidth,
          resourcePanelOpen && styles.resourcePanelOpen,
          hasOnlyOneColumn && styles.monoView,
          !sidebarExpanded && styles.sidebarCondensed,
          isResizing && styles.resizing,
          mobileSecondaryColumnDirection === 'vertical' && styles.verticalSecondaryColumn,
          className,
        )}
        data-testid={dataTestId}
        ref={containerRef}
        {...props}
      >
        {faviconOverride && <span data-favicon-override={faviconOverride} data-testid="favicon-override" />}
        {slots.sidebar}
        {slots.header || (hideDefaultSidebarToggle ? null : <HeaderToggleOnly />)}
        {slots.columnLeft}
        {slots.columnRight}
      </div>
    </LayoutContext>
  )
}

export const Layout = Object.assign(LayoutRoot, {
  Banner,
  Sidebar: LayoutSidebar,
  ColumnLeft,
  ColumnRight,
  Header,
  HeaderLeft,
  HeaderRight,
})
